import React from 'react';
import {
  Drawer,
  Box,
  Button,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Error as ErrorIcon, Warning as WarningIcon, CheckCircle as CheckIcon, CloudSync as CloudSyncIcon, AutoAwesome as AssistantIcon } from '@mui/icons-material';

const IssueRow = ({ message, severity, onClick, secondary }) => (
  <ListItemButton onClick={onClick} dense alignItems="flex-start" disableRipple={!onClick}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      {severity === 'error' ? (
        <ErrorIcon color="error" fontSize="small" />
      ) : (
        <WarningIcon color="warning" fontSize="small" />
      )}
      <ListItemText
        primary={message}
        secondary={secondary}
        primaryTypographyProps={{ variant: 'body2' }}
      />
    </Box>
  </ListItemButton>
);

// Sums the Meta-side issues into the panel's summary. `meta` is the flow's sync/validation status
// (from useFlowSyncStatus); its `validationErrors.errors` are what Meta flagged on the last sync.
const metaIssuesOf = (meta) => meta?.validationErrors?.errors || [];

// One line describing where Meta's authoritative validation stands, since it lags the live builder
// checks (it only settles after a save reaches Meta).
const MetaSection = ({ meta }) => {
  const issues = metaIssuesOf(meta);
  const syncStatus = meta?.syncStatus;

  let body;
  if (!meta || !syncStatus) {
    body = <IssueRow severity="warning" message="Save the flow to validate it against WhatsApp." />;
  } else if (syncStatus === 'PENDING' || syncStatus === 'SYNCING') {
    body = <IssueRow severity="warning" message="Checking with WhatsApp…" />;
  } else if (syncStatus === 'FAILED') {
    body = <IssueRow severity="error" message={meta.syncError || "Couldn't reach WhatsApp. It will retry."} />;
  } else if (issues.length === 0) {
    body = (
      <ListItemButton dense disableRipple>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon color="success" fontSize="small" />
          <ListItemText primary="WhatsApp validated this flow. No issues." primaryTypographyProps={{ variant: 'body2' }} />
        </Box>
      </ListItemButton>
    );
  } else {
    body = issues.map((issue, idx) => (
      <IssueRow key={`meta-${idx}`} severity="error" message={issue.message || String(issue)} />
    ));
  }

  return (
    <li>
      <ul>
        <ListSubheader sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CloudSyncIcon fontSize="small" /> WhatsApp validation
        </ListSubheader>
        {body}
      </ul>
    </li>
  );
};

const ValidationSummaryPanel = ({ open, onClose, validationMap, screens, meta, onJumpToComponent, onJumpToScreen, onFixWithAssistant }) => {
  const { byComponent, byScreen, totals } = validationMap || {
    byComponent: new Map(),
    byScreen: new Map(),
    totals: { errorCount: 0, warningCount: 0 },
  };

  // Group by screen for readable browsing.
  const sections = (screens || []).map((screen, index) => {
    const screenIssues = byScreen.get(screen.id);
    const componentIssues = [];
    byComponent.forEach((entry, componentId) => {
      if (entry.screenId === screen.id) {
        componentIssues.push({ componentId, ...entry });
      }
    });
    const hasAny = !!screenIssues || componentIssues.length > 0;
    return { screen, index, screenIssues, componentIssues, hasAny };
  });

  const metaIssues = metaIssuesOf(meta);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 380, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      data-testid="validation-summary-panel"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography variant="h6">Validation</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close validation panel">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {totals.errorCount === 0 && totals.warningCount === 0 && metaIssues.length === 0
        && meta?.syncStatus === 'SYNCED' && meta?.validationStatus !== 'INVALID' ? (
          <>
            <CheckIcon color="success" fontSize="small" />
            <Typography variant="body2">No issues. The flow looks good.</Typography>
          </>
        ) : (
          <>
            {totals.errorCount > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${totals.errorCount} error${totals.errorCount === 1 ? '' : 's'}`}
                color="error"
                size="small"
              />
            )}
            {totals.warningCount > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${totals.warningCount} warning${totals.warningCount === 1 ? '' : 's'}`}
                color="warning"
                size="small"
              />
            )}
            {metaIssues.length > 0 && (
              <Chip
                icon={<CloudSyncIcon />}
                label={`${metaIssues.length} from WhatsApp`}
                color="error"
                size="small"
                variant="outlined"
              />
            )}
            {totals.errorCount === 0 && totals.warningCount === 0 && metaIssues.length === 0 && (
              <Typography variant="body2" color="text.secondary">Builder checks pass. See the WhatsApp status below.</Typography>
            )}
          </>
        )}
      </Box>
      {/* Errors, from here or from WhatsApp, can be handed to the assistant as one message: the
          same list a person would otherwise read and retype. Warnings are advice, not work. */}
      {onFixWithAssistant && (totals.errorCount > 0 || metaIssues.length > 0) && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            startIcon={<AssistantIcon />}
            onClick={onFixWithAssistant}
            data-testid="fix-with-assistant"
          >
            Fix these with the assistant
          </Button>
        </Box>
      )}
      <Divider />
      <List
        subheader={<li />}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', '& ul': { padding: 0 } }}
      >
        <MetaSection meta={meta} />
        {/* Findings about the flow as a whole: a screen nothing leads to, a routing rule WhatsApp
            refuses. Not tied to one component, so they get their own heading. */}
        {((validationMap?.flowIssues?.errors?.length || 0) + (validationMap?.flowIssues?.warnings?.length || 0)) > 0 && (
          <li>
            <ul>
              <ListSubheader>Whole flow</ListSubheader>
              {validationMap.flowIssues.errors.map((message, idx) => (
                <IssueRow key={`flow-err-${idx}`} message={message} severity="error" />
              ))}
              {validationMap.flowIssues.warnings.map((message, idx) => (
                <IssueRow key={`flow-warn-${idx}`} message={message} severity="warning" />
              ))}
            </ul>
          </li>
        )}
        {sections.filter((s) => s.hasAny).map(({ screen, index, screenIssues, componentIssues }) => (
          <li key={screen.id}>
            <ul>
              <ListSubheader>{screen.title || `Screen ${index + 1}`}</ListSubheader>

                {screenIssues?.errors?.map((message, idx) => (
                  <IssueRow
                    key={`screen-err-${idx}`}
                    message={message}
                    severity="error"
                    onClick={onJumpToScreen ? () => onJumpToScreen(screen.id) : undefined}
                  />
                ))}
                {screenIssues?.warnings?.map((message, idx) => (
                  <IssueRow
                    key={`screen-warn-${idx}`}
                    message={message}
                    severity="warning"
                    onClick={onJumpToScreen ? () => onJumpToScreen(screen.id) : undefined}
                  />
                ))}

                {componentIssues.map((entry) => (
                  <React.Fragment key={entry.componentId}>
                    {entry.errors.map((message, idx) => (
                      <IssueRow
                        key={`comp-err-${entry.componentId}-${idx}`}
                        message={message}
                        severity="error"
                        secondary={entry.componentType}
                        onClick={onJumpToComponent ? () => onJumpToComponent(entry.screenId, entry.componentId) : undefined}
                      />
                    ))}
                    {entry.warnings.map((message, idx) => (
                      <IssueRow
                        key={`comp-warn-${entry.componentId}-${idx}`}
                        message={message}
                        severity="warning"
                        secondary={entry.componentType}
                        onClick={onJumpToComponent ? () => onJumpToComponent(entry.screenId, entry.componentId) : undefined}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </ul>
            </li>
          ))}
        </List>
    </Drawer>
  );
};

export default ValidationSummaryPanel;
